import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  FormContainer,
  FormInput,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useState } from "react";
import { IMarComAutomationTimeProps } from "./models";
import {
  createMarComAutomationTime,
  getMarComAutomationTimeById,
  updateMarComAutomationTime,
} from "../../../../../../services/marComAutomationTimes.service";
import { timeRuleSchema, TTimeRuleForm } from "./timeRulesSchema";
import { listTimeClassification } from "../../../../../../services/ticketAutomationTimeClassification.service";
import { getTypeofTimeAutomationRuleByCode } from "../../../../../../services/timeRule.service";

const translationPath = "page.4uMarCom.automationRules.marcom_timerule.";

const TimeRules = ({
  id,
  marComAutomationRuleId,
  onBackButton,
}: IMarComAutomationTimeProps) => {
  const { t } = useTranslation();

  const [optionsClassification, setOptionsClassification] = useState<
    ISelectType[]
  >([]);

  const [optionsType, setOptionsType] = useState<ISelectType[]>([]);

  const { sendRequest: save, loading: sLoading } = useFetch(
    createMarComAutomationTime
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateMarComAutomationTime
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getMarComAutomationTimeById
  );

  const updateHandler = (v: TTimeRuleForm, id: string) =>
    update({
      item: {
        id: id,
        value: Number(v.value),
        marComAutomationRuleId: marComAutomationRuleId ?? "",
        ticketAutomationTimeClassificationId: v.timeClassification
          ? v.timeClassification.id
          : "",
        typeofTypeFractionId: v.type!.id,
      },
      id: id,
    });

  const saveHandler = (v: TTimeRuleForm) =>
    save({
      value: Number(v.value),
      marComAutomationRuleId: marComAutomationRuleId ?? "",
      ticketAutomationTimeClassificationId: v.timeClassification
        ? v.timeClassification.id
        : "",
      typeofTypeFractionId: v.type!.id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TTimeRuleForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TTimeRuleForm = {
          ...data,
          value: data.value.toString(),
          timeClassification: {
            id: data.ticketAutomationTimeClassification.id,
            name: data.ticketAutomationTimeClassification.timeClassification
              .name,
          },
          type: data.typeofTypeFraction,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );
  const { sendRequest } = useFetch(listTimeClassification);

  const fetchTimeClassifications = useCallback(async () => {
    const { data, success } = await sendRequest({
      TicketAutomationCode: "15",
    });
    if (data && success) {
      setOptionsClassification(
        data.map((d) => ({ id: d.id, name: d.timeClassification.name }))
      );
    }
  }, [sendRequest]);

  useEffect(() => {
    fetchTimeClassifications();
  }, [fetchTimeClassifications]);

  const { sendRequest: fetchTypeofTimeFractionsRequest } = useFetch(
    getTypeofTimeAutomationRuleByCode
  );

  const fetchTypeofTimeFractions = useCallback(async () => {
    const { data, success } = await fetchTypeofTimeFractionsRequest("15");
    if (data && success) {
      let options = data;
      setOptionsType(options.map((d) => ({ id: d.id, name: d.name })));
    }
  }, [fetchTypeofTimeFractionsRequest]);

  useEffect(() => {
    fetchTypeofTimeFractions();
  }, [fetchTypeofTimeFractions]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TTimeRuleForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "time_rule")}
      schema={timeRuleSchema}
      subtitle={t(translationPath + "new_time_rule")}
      subtitleWatchField={"value"}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <AutoComplete
              name="timeClassification"
              label={
                t(translationPath + "time_classification") ||
                "Time Classification"
              }
              getOptionLabel={(option) => option.name}
              size="small"
              fullWidth
              options={optionsClassification}
            />
          </Grid>

          <Grid item xs={12} sm={5}>
            <AutoComplete
              name="type"
              label={t(translationPath + "type") || "Type"}
              getOptionLabel={(option) => option.name}
              size="small"
              fullWidth
              options={optionsType}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormInput
              size="small"
              name="value"
              fullWidth
              mask={/^[0-9]*$/}
              label={t(translationPath + "value") || "Value"}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default TimeRules;
